// =============================================================================
// domain/customWidgets.ts — a custom widget's config, and every edit to it.
// -----------------------------------------------------------------------------
// Relocated from the old client's `customWidgets()` plus the per-control click
// handlers that mutate the array in place. The BEHAVIOUR is unchanged; the shape
// is not — every function here returns a NEW widget instead of mutating one,
// because React renders from the value it is given and an in-place edit is
// invisible to it.
//
// ── 🚨 THREE ARRAYS THAT MUST STAY THE SAME LENGTH ──
//
// `metrics`, `chartTypes` and `colors` are parallel: index 2 of each describes
// the same series. Removing a metric without removing its chart type shifts
// every series after it — the third metric silently adopts the fourth's shape
// and colour, on a chart that still looks entirely reasonable. Every edit below
// touches all three together, and `normalizeWidget` repairs a stored widget
// whose arrays have drifted.
//
// That drift is not hypothetical: the old client stored a single `metric` and a
// single `chart` before overlays existed, so a widget saved back then arrives
// with neither array. Those widgets are in `preferences` on real accounts.
//
// ── ⚠ `directional` IS CLEARED BY THREE DIFFERENT EDITS ──
//
// The long/short split only means something on ONE metric that supports it, so
// adding an overlay clears it, and so does switching to a metric that is not
// directional. `domain/customSeries` refuses to apply it either way — but a
// toggle left lit next to a chart that ignores it is its own bug.
// =============================================================================

import { CUSTOM_METRICS, metricById } from './customMetrics';
import type { ChartType, GroupBy } from './customSeries';

/** ⚠ FOUR. Past that the legend wraps and the axes have nowhere to go. */
export const CW_MAX_OVERLAY = 4;

export interface CustomWidget {
  metrics: string[];
  chartTypes: ChartType[];
  groupBy: GroupBy;
  cumulative: boolean;
  directional: boolean;
  /** Per-series overrides. Sparse — an unset entry falls back to the palette. */
  colors?: string[];
}

/**
 * The four widgets a trader who has never touched this tab sees.
 *
 * ⚠ These are SEEDED INTO STORAGE by the old client on first read, not treated
 * as a fallback. So they are not "the empty state" — they are the first four
 * widgets, and a trader who deletes one has deleted it for good.
 */
export const DEFAULT_WIDGETS: readonly CustomWidget[] = [
  { metrics: ['net_pnl'], chartTypes: ['area'], groupBy: 'day', cumulative: true, directional: false },
  { metrics: ['avg_win', 'avg_loss'], chartTypes: ['bar', 'bar'], groupBy: 'day', cumulative: false, directional: false },
  { metrics: ['win_rate'], chartTypes: ['bar'], groupBy: 'week', cumulative: false, directional: true },
  { metrics: ['hold_time'], chartTypes: ['bar'], groupBy: 'day', cumulative: false, directional: false },
];

const CHART_TYPES: readonly ChartType[] = ['line', 'area', 'bar'];
const GROUPS: readonly GroupBy[] = ['day', 'week', 'month'];

const asChart = (v: unknown): ChartType => (
  CHART_TYPES.includes(v as ChartType) ? (v as ChartType) : 'line'
);

/**
 * A stored widget → a widget this client can render.
 *
 * ⚠ THE LEGACY MIGRATION IS THE POINT. A widget saved before overlays existed
 * has `metric: 'net_pnl'` and `chart: 'bar'` and neither array, and the old
 * client repairs it on every read rather than in a migration — so the repaired
 * shape has never been written back for some accounts. Dropping this would take
 * those widgets down to the default metric with the default shape, silently.
 *
 * ⚠ `chartTypes` is rebuilt whenever its length disagrees with `metrics`, and
 * new entries inherit the OLD widget-level `chart`, not `'line'` — otherwise a
 * legacy bar widget comes back as a line.
 */
export function normalizeWidget(raw: unknown): CustomWidget {
  const w = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;

  const metrics = Array.isArray(w['metrics']) && w['metrics'].length
    ? (w['metrics'] as unknown[]).map((m) => String(m))
    : [typeof w['metric'] === 'string' && w['metric'] ? w['metric'] : 'net_pnl'];

  const legacy = asChart(w['chart']);
  const prev = Array.isArray(w['chartTypes']) ? (w['chartTypes'] as unknown[]) : [];
  const chartTypes = (Array.isArray(w['chartTypes']) && prev.length === metrics.length)
    ? prev.map(asChart)
    : metrics.map((_, i) => (prev[i] !== undefined ? asChart(prev[i]) : legacy));

  const colors = Array.isArray(w['colors'])
    // ⚠ `?? ''`, not a filter. A HOLE at index 0 must stay index 0 or every
    // later colour shifts onto the wrong series.
    ? (w['colors'] as unknown[]).map((c) => (typeof c === 'string' ? c : ''))
    : undefined;

  const out: CustomWidget = {
    metrics,
    chartTypes,
    groupBy: GROUPS.includes(w['groupBy'] as GroupBy) ? (w['groupBy'] as GroupBy) : 'day',
    cumulative: w['cumulative'] === true,
    directional: w['directional'] === true,
  };
  if (colors) out.colors = colors;
  return out;
}

/** ⚠ Cumulative by DEFAULT, and a line — not the seeded first widget's area. */
export function newWidget(): CustomWidget {
  return {
    metrics: ['net_pnl'], chartTypes: ['line'], groupBy: 'day',
    cumulative: true, directional: false,
  };
}

/** The resolved metrics of a widget, in order. Always at least one. */
export function widgetSeries(w: CustomWidget) {
  return (w.metrics.length ? w.metrics : ['net_pnl']).map(metricById);
}

export function changeMetric(w: CustomWidget, index: number, id: string): CustomWidget {
  const metrics = w.metrics.slice();
  metrics[index] = id;
  // ⚠ A metric that cannot be split takes the toggle down with it.
  const stillDirectional = w.directional
    && metrics.length === 1
    && !!metricById(metrics[0]).directional;
  return { ...w, metrics, directional: stillDirectional };
}

export function changeSeriesChart(w: CustomWidget, index: number, chart: ChartType): CustomWidget {
  const chartTypes = w.chartTypes.slice();
  chartTypes[index] = chart;
  return { ...w, chartTypes };
}

export function changeGroupBy(w: CustomWidget, groupBy: GroupBy): CustomWidget {
  return { ...w, groupBy };
}

export function toggleCumulative(w: CustomWidget): CustomWidget {
  return { ...w, cumulative: !w.cumulative };
}

export function toggleDirectional(w: CustomWidget): CustomWidget {
  return { ...w, directional: !w.directional };
}

/**
 * Add the first catalogue metric this widget does not already carry.
 *
 * ⚠ CATALOGUE ORDER, not alphabetical and not random — overlaying twice on a
 * fresh widget gives Net P&L then Avg trade P&L every time, which is what makes
 * the button predictable. Returns the widget unchanged when it is full or when
 * every metric is already on it.
 */
export function addOverlay(w: CustomWidget): CustomWidget {
  if (w.metrics.length >= CW_MAX_OVERLAY) return w;
  const add = CUSTOM_METRICS.find((m) => !w.metrics.includes(m.id));
  if (!add) return w;
  return {
    ...w,
    metrics: [...w.metrics, add.id],
    chartTypes: [...w.chartTypes, 'line'],
    // ⚠ An overlay and a long/short split cannot both be on.
    directional: false,
  };
}

/** ⚠ Refuses to remove the last metric — a widget with no metric charts nothing. */
export function removeMetric(w: CustomWidget, index: number): CustomWidget {
  if (w.metrics.length <= 1) return w;
  const metrics = w.metrics.slice();
  const chartTypes = w.chartTypes.slice();
  metrics.splice(index, 1);
  chartTypes.splice(index, 1);
  const out: CustomWidget = { ...w, metrics, chartTypes };
  // ⚠ ALL THREE ARRAYS. See the header.
  if (Array.isArray(w.colors)) {
    const colors = w.colors.slice();
    colors.splice(index, 1);
    out.colors = colors;
  }
  return out;
}

/**
 * Set one series' colour.
 *
 * ⚠ PADS WITH EMPTY STRINGS rather than leaving holes. `['', '', '#f00']` and a
 * three-hole sparse array read the same downstream — `colors[i] || palette[i]`
 * — but the padded one survives a round trip through JSON, and this array is
 * stored in preferences.
 */
export function recolorSeries(w: CustomWidget, index: number, color: string): CustomWidget {
  const colors = (w.colors ?? []).slice();
  while (colors.length <= index) colors.push('');
  colors[index] = color;
  return { ...w, colors };
}

// ---------------------------------------------------------------------------
// Where the widgets live: `preferences.app_flags.customWidgets`
// ---------------------------------------------------------------------------
//
// 🚨 THE ONE WAY THIS FEATURE CAN DESTROY DATA, and it is the same shape as the
// leak loop's — see `domain/leakLoop.ts`, which says it at length.
//
// `app_flags` is ONE JSONB blob shared by every surface in BOTH clients. It also
// holds `leakLoop`, `settingsLayout`, `journalLayout`, `tradeFormLayout`,
// `pnlDisplayUnit`, the tour flags and whatever the old client adds next — 16
// keys on Josh's row today. A save writes the WHOLE column, so a writer that
// builds `{ customWidgets: … }` does not "save a widget". It deletes every other
// preference the trader has ever set, from a screen that mentions none of them.
//
// ⚠ AND THE VALUE HERE IS A COLLECTION, which the leak loop's hazard is not to
// the same degree (D141). Two widgets added without a reload between them, both
// spreading the same starting array, means the second erases the first. The
// array has to be re-read at the moment of the write, never closed over.

/** The key beta writes. Both clients read the same row. */
export const CUSTOM_WIDGETS_KEY = 'customWidgets';

/**
 * The stored widgets, normalised — or the four defaults when there are none.
 *
 * 🚨 THE DEFAULTS ARE **NOT** SEEDED INTO STORAGE HERE, and that is a divergence
 * from the old client with a reason.
 *
 * Beta assigns them into `state.settings.appFlags` on first read, so the next
 * save of anything at all persists them. This client returns them without
 * writing, and the first real edit is what puts an array on disk.
 *
 * ⚠ Writing on READ is how a page load becomes a save. It would mean opening the
 * tab writes to `app_flags` — the one column where a bad write costs every other
 * setting — for a user who only looked at it. The rendered screen is identical
 * either way; what differs is whether looking at a tab can lose data.
 */
export function readWidgets(
  appFlags: Record<string, unknown> | null | undefined,
): CustomWidget[] {
  const raw = (appFlags ?? {})[CUSTOM_WIDGETS_KEY];
  if (!Array.isArray(raw)) return DEFAULT_WIDGETS.map((d) => normalizeWidget(d));
  // ⚠ An EMPTY stored array is a trader who deleted every widget, not a trader
  // who has none. Falling back to the defaults there would resurrect four cards
  // they removed one at a time.
  return raw.map(normalizeWidget);
}

/**
 * Old flags in, NEW flags out. Pure — nothing here reaches the network.
 *
 * ⚠ SPREADS THE STORED BLOB. Every key it does not recognise survives untouched.
 */
export function withWidgets(
  appFlags: Record<string, unknown> | null | undefined,
  widgets: readonly CustomWidget[],
): Record<string, unknown> {
  return { ...(appFlags ?? {}), [CUSTOM_WIDGETS_KEY]: widgets.map((w) => ({ ...w })) };
}
